import React, { memo } from 'react';

import cn from 'classnames';

import { isClientAttachment } from '@entities/attachment/lib';
import { TAttachmentDTOCamelCased, TAttachmentOnClient } from '@entities/attachment/model/types';
import { CircularLoader, useLightBoxControl } from '@shared/ui';

import cls from './FileItem.module.css';
import { FileItemActions } from './FileItemActions';
import { FileItemImg } from './FileItemImg';
import { FileItemSvg } from './FileItemSvg';
import { useFileItem } from '../../lib/hooks';

interface IFileItem {
    fileId: number;
    fileName: string;
    fileExtension: string;
    imgUrl: string;
    isImage: boolean;
    isLoading: boolean;
    ContextMenu: React.ReactNode;
    anchorRef: (el: HTMLElement | null) => void;
    isContextMenuOpen?: boolean;
    toggleContextMenu?: () => void;
    attachments: (TAttachmentOnClient | TAttachmentDTOCamelCased)[];
}

export const FileItem = memo(
    ({
        fileName,
        fileExtension,
        imgUrl,
        isImage,
        isLoading,
        ContextMenu,
        anchorRef,
        isContextMenuOpen,
        toggleContextMenu,
        attachments,
        fileId,
    }: IFileItem) => {
        const { onFileItemClick, onFileItemMouseEnter, toggleHover, isHovered } = useFileItem();
        const { onShowLightBox } = useLightBoxControl();
        const filesForLightbox = attachments.map((attachment) => {
            const isClient = isClientAttachment(attachment);
            if (isClient) {
                return {
                    id: attachment.id,
                    fileSrc: URL.createObjectURL(attachment.file),
                    mimeType: attachment.file.type,
                    fileName: attachment.file.name,
                };
            }
            return {
                id: attachment.id,
                fileSrc: attachment.originalUrl,
                mimeType: attachment.mimeType,
                fileName: attachment.name,
            };
        });

        return (
            <li
                className={cls.fileItemWrapper}
                onClick={onFileItemClick}
                onMouseEnter={onFileItemMouseEnter}
                onMouseLeave={toggleHover}
                draggable={false}
            >
                <span
                    className={cn(cls.fileItem, {
                        [cls.isHovered]: isHovered || isContextMenuOpen,
                    })}
                    onClick={() =>
                        onShowLightBox({
                            id: fileId,
                            newFiles: filesForLightbox,
                        })
                    }
                >
                    {isLoading && <CircularLoader size={26} />}
                    {!isLoading && isImage && <FileItemImg imgUrl={imgUrl} fileName={fileName} />}
                    {!isLoading && !isImage && <FileItemSvg extension={fileExtension} />}

                    <div className={cls.fileInfoWrapper}>
                        <p className={cls.fileName}>{fileName}</p>
                        <span className={cls.fileExtension}>.{fileExtension}</span>
                    </div>
                </span>
                {ContextMenu && (
                    <FileItemActions
                        onClickHandler={toggleContextMenu}
                        innerRef={anchorRef}
                        isVisible={isHovered}
                    />
                )}

                {isContextMenuOpen && ContextMenu}
            </li>
        );
    }
);
